"use client";

import { useTranslations } from "next-intl";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { LabOrderStatus } from "@/types";

// Static literal-key map (T-73-05: no dynamic translator arg) — values are
// compile-time literals resolved via the STATUS_KEY lookup.
const STATUS_KEY: Record<LabOrderStatus, string> = {
  draft: "status.draft",
  sent: "status.sent",
  metal_tryin_ready: "status.metalTryinReady",
  dentin_tryin_ready: "status.dentinTryinReady",
  ready: "status.ready",
  delivered: "status.delivered",
  cancelled: "status.cancelled",
};

// English-only fallback labels — kept for the `labOrderStatusLabel` export
// (a non-rendering helper used outside React render). UI rendering goes
// through the i18n `STATUS_KEY` map above.
const STATUS_LABEL: Record<LabOrderStatus, string> = {
  draft: "Draft",
  sent: "Sent",
  metal_tryin_ready: "Metal try-in ready",
  dentin_tryin_ready: "Dentin try-in ready",
  ready: "Ready",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

interface Props {
  status: LabOrderStatus;
  className?: string;
}

/**
 * Single source of truth for lab-order status → token mapping.
 * Per UI-SPEC §"Status badge color mapping (locked)".
 */
export function LabOrderStatusBadge({ status, className }: Props) {
  const t = useTranslations("labOrders");
  const label = t(STATUS_KEY[status]);

  switch (status) {
    case "draft":
      return (
        <Badge
          variant="outline"
          className={cn("text-muted-foreground", className)}
        >
          {label}
        </Badge>
      );
    case "sent":
      return (
        <Badge variant="default" className={className}>
          {label}
        </Badge>
      );
    case "metal_tryin_ready":
      return (
        <Badge
          variant="outline"
          className={cn(
            "border-warning/50 bg-warning/10 text-warning",
            className,
          )}
        >
          {label}
        </Badge>
      );
    case "dentin_tryin_ready":
      return (
        <Badge
          variant="outline"
          className={cn(
            "border-warning/50 bg-warning/10 text-warning",
            className,
          )}
        >
          {label}
        </Badge>
      );
    case "ready":
      return (
        <Badge
          variant="outline"
          className={cn(
            "border-success/50 bg-success/10 text-success",
            className,
          )}
        >
          {label}
        </Badge>
      );
    case "delivered":
      return (
        <Badge variant="secondary" className={className}>
          {label}
        </Badge>
      );
    case "cancelled":
      return (
        <Badge variant="destructive" className={className}>
          {label}
        </Badge>
      );
  }
}

/**
 * English-only status label helper (non-rendering). For UI rendering use
 * <LabOrderStatusBadge> which resolves the localized label via next-intl.
 */
export function labOrderStatusLabel(status: LabOrderStatus): string {
  return STATUS_LABEL[status];
}
